import {
    type ApiContainerAction,
    type ApiContainerStockEntry,
    type ApiPlannedContainerAction,
    EnumContainerActionRequest,
} from '@Model';
import {
    Badge,
    FlexColumn,
    FlexRow,
    FloatingCard,
    renderDateTime,
    useTranslate,
} from '@synqo/frontend-component-library';
import { getStandingDurationLabel } from '@utils/standingDuration';
import { useMemo } from 'react';
import { ACTION_NOUN, type ContainerActionKind } from './containerActionKind';
import { type ContainerPlanSource } from './containerPlanSource';
import { type SiteCorrection, withRenumberedOpen } from './siteCorrection';
import classes from './ContainersAtSite.module.css';
import { FollowUpButtons } from './FollowUpButtons';
import { OrderLink } from './OrderLink';
import { findDepartures, findDoublePlannings } from './doublePlanning';
import {
    type ComingCard,
    type OtherOrder,
    type OwnPlacementCard,
    type PlannedElsewhere,
    plannedElsewhereOf,
    type StockCard,
    toComingCards,
    toOwnPlacementCards,
    toStockCards,
} from './siteCards';
import { type ContainerTypeDetailsByTypeId } from './useContainerTypeDetails';

/** Whether the cards plan into the order, show their buttons locked, or show none. */
export type SiteCardActions = 'edit' | 'locked' | 'none';

interface ContainersAtSiteCardProps {
    standing: ApiContainerStockEntry[];
    planned: ApiPlannedContainerAction[];
    plannedFailed?: boolean;
    /** Why the site could not be read, shown instead of claiming it is empty. */
    failure?: string;
    /** Whether the stock is shown as it will be once the order is saved, which offers nothing to plan from. */
    afterSave?: boolean;
    correction?: SiteCorrection;
    orderId: string;
    orderNumber: string;
    containerActions: ApiContainerAction[];
    containerTypes: ContainerTypeDetailsByTypeId;
    actions: SiteCardActions;
    /** Whether a planning call is in flight, which locks the buttons. */
    planning: boolean;
    onPlan: (action: EnumContainerActionRequest, source: ContainerPlanSource) => void;
    onOpenOrder: (orderId: string) => void;
}

const HANDLED: Record<ContainerActionKind, string> = {
    place: 'in diesem Auftrag: Stellung',
    collect: 'in diesem Auftrag: Abholung',
    swap: 'in diesem Auftrag: Tausch',
    empty: 'in diesem Auftrag: Leerung',
};

const CONFIRMED: Record<ContainerActionKind, string> = {
    place: 'in diesem Auftrag: Stellung bestätigt',
    collect: 'in diesem Auftrag: Abholung bestätigt',
    swap: 'in diesem Auftrag: Tausch bestätigt',
    empty: 'in diesem Auftrag: Leerung bestätigt',
};

const LOCKED_HINT = 'Erst „Bearbeiten“ wählen';

const DOUBLE_PLANNED = 'Doppelplanung?';

const LEAVING_HINT = 'Abholung oder Tausch bereits geplant';

const NOTHING_ELSEWHERE: PlannedElsewhere = { orders: [], count: 0, removedCount: 0 };

/** "Am Standort": the containers standing at the site and the placements other open orders plan there. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function ContainersAtSiteCard({
    standing,
    planned,
    plannedFailed = false,
    failure = '',
    afterSave = false,
    correction,
    orderId,
    orderNumber,
    containerActions,
    containerTypes,
    actions,
    planning,
    onPlan,
    onOpenOrder,
}: ContainersAtSiteCardProps) {
    const i18n = useTranslate('');
    const order = useMemo(() => ({ id: orderId, number: orderNumber }), [orderId, orderNumber]);
    const followUps = useMemo(() => withRenumberedOpen(containerActions, correction), [containerActions, correction]);
    const stockCards = useMemo(
        () => toStockCards(standing, containerTypes, followUps),
        [standing, containerTypes, followUps],
    );
    const comingCards = useMemo(
        () => toComingCards(planned, order, followUps, stockCards),
        [planned, order, followUps, stockCards],
    );
    const ownCards = useMemo(() => toOwnPlacementCards(containerActions), [containerActions]);
    const elsewhere = useMemo(
        () => new Map(stockCards.map((card) => [card.key, plannedElsewhereOf(card, planned, order)])),
        [stockCards, planned, order],
    );
    const doublePlannings = useMemo(
        () => findDoublePlannings({ stockCards, ownCards, comingCards, elsewhere }),
        [stockCards, ownCards, comingCards, elsewhere],
    );
    const departures = useMemo(
        () => findDepartures({ stockCards, ownCards, comingCards, elsewhere }, planned, order),
        [stockCards, ownCards, comingCards, elsewhere, planned, order],
    );
    const staying = correction?.staying ?? [];
    const corrects = staying.length > 0 || (correction?.notes.size ?? 0) > 0;
    const isEmpty =
        stockCards.length === 0 &&
        ownCards.length === 0 &&
        staying.length === 0 &&
        comingCards.length === 0 &&
        !plannedFailed;

    const buttons = (
        key: string,
        followUps: EnumContainerActionRequest[],
        onFollowUp: (kind: EnumContainerActionRequest) => void,
    ) =>
        actions !== 'none' &&
        !afterSave && (
            <FlexRow gap="xxs" wrap="wrap">
                <FollowUpButtons
                    allowedFollowUps={followUps}
                    locked={planning || actions === 'locked'}
                    tooltip={actions === 'locked' ? i18n(LOCKED_HINT) : undefined}
                    leavingHint={departures.has(key) ? i18n(LEAVING_HINT) : undefined}
                    size="xs"
                    onFollowUp={onFollowUp}
                />
            </FlexRow>
        );

    return (
        <FloatingCard
            title={i18n('Am Standort')}
            additionalHeaderItems={!failure && <Badge size="xs">{standing.length}</Badge>}
        >
            <FlexColumn gap="xs" align="stretch">
                {failure && <FlexRow className={classes.warning}>{failure}</FlexRow>}
                {isEmpty && !failure && (
                    <FlexRow className={classes.nothing}>{i18n('Kein Container am Standort.')}</FlexRow>
                )}

                {(afterSave || corrects) && (
                    <FlexRow className={classes.group}>
                        {i18n(corrects ? 'Bestand nach der Korrektur' : 'Bestand nach dem Speichern')}
                    </FlexRow>
                )}
                {stockCards.map((card) => {
                    const note = card.group.lines
                        .map((line) => correction?.notes.get(line.container.foreign_key ?? ''))
                        .find(Boolean);

                    return (
                        <StockCardView
                            key={card.key}
                            onOpenOrder={onOpenOrder}
                            card={card}
                            note={note}
                            elsewhere={elsewhere.get(card.key) ?? NOTHING_ELSEWHERE}
                            doublePlanned={doublePlannings.stock.has(card.key)}
                            buttons={
                                !note &&
                                !card.isFullyHandled &&
                                card.openStandingTimeIds.length > 0 &&
                                buttons(card.key, card.followUps, (kind) =>
                                    onPlan(kind, { standingTimeIds: card.openStandingTimeIds }),
                                )
                            }
                        />
                    );
                })}

                {ownCards.map((card) => (
                    <OwnPlacementCardView
                        key={card.key}
                        card={card}
                        alike={doublePlannings.placements.get(card.key) ?? []}
                        onOpenOrder={onOpenOrder}
                    />
                ))}
                {staying.map((action) => (
                    <ActionCardView
                        key={action.id}
                        action={action}
                        flag={i18n('bleibt stehen · Korrektur')}
                        flagClassName={classes.correction}
                    />
                ))}

                {(comingCards.length > 0 || plannedFailed) && (
                    <FlexRow className={classes.group}>{i18n('Geplant in anderen Aufträgen')}</FlexRow>
                )}
                {plannedFailed && (
                    <FlexRow className={classes.warning}>
                        {i18n('Die geplanten Container konnten nicht geladen werden.')}
                    </FlexRow>
                )}
                {comingCards.map((card) => (
                    <ComingCardView
                        key={card.key}
                        onOpenOrder={onOpenOrder}
                        card={card}
                        buttons={
                            !card.isFullyHandled &&
                            buttons(card.key, card.planned.allowed_follow_ups, (kind) =>
                                onPlan(kind, {
                                    plannedAction: card.planned,
                                    count: Math.max(1, card.alike.length - card.takenInPresentOrder),
                                }),
                            )
                        }
                    />
                ))}
            </FlexColumn>
        </FloatingCard>
    );
}

interface StockCardViewProps {
    card: StockCard;
    /** What the correction does to this container. Shown instead of the usual note. */
    note: string | undefined;
    onOpenOrder: (orderId: string) => void;
    elsewhere: PlannedElsewhere;
    doublePlanned: boolean;
    buttons: React.ReactNode;
}

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function StockCardView({ card, note, elsewhere, doublePlanned, buttons, onOpenOrder }: StockCardViewProps) {
    const i18n = useTranslate('');
    const { group } = card;

    const title =
        group.count > 1
            ? `${group.count} × ${group.container_type.display_string}`
            : withNumber(group.container_type.display_string, group.container.display_string);

    return (
        <FlexColumn label={title} gap="xxs" align="stretch" className={classes.card}>
            <FlexRow className={classes.cardTitle}>{title}</FlexRow>
            <FlexRow className={classes.meta}>
                {[
                    group.count > 1 && group.container.display_string && `Nr. ${group.container.display_string}`,
                    group.content.display_string,
                    `seit ${renderDateTime(group.standing_since, 'date')}`,
                    getStandingDurationLabel(group.standing_since, undefined),
                ]
                    .filter(Boolean)
                    .join(' · ')}
            </FlexRow>
            {note && <FlexRow className={classes.correction}>{note}</FlexRow>}
            {!note && card.handledInPresentOrder && (
                <FlexRow className={classes.flag}>{handledText(card, i18n)}</FlexRow>
            )}
            {doublePlanned && <FlexRow className={classes.doublePlanned}>{i18n(DOUBLE_PLANNED)}</FlexRow>}
            {!card.isPool &&
                elsewhere.orders.map((other) => (
                    <FlexRow key={other.orderId} gap="xxs" className={classes.muted}>
                        {`${i18n(ACTION_NOUN[other.kind])} ${i18n('geplant in')}`}
                        <OrderLink orderId={other.orderId} number={other.number} accent onOpen={onOpenOrder} />
                    </FlexRow>
                ))}
            {card.isPool && elsewhere.count > 0 && (
                <FlexRow gap="xxs" wrap="wrap" align="baseline" className={classes.muted}>
                    {`${elsewhere.count} ${i18n('verplant in')}`}
                    {elsewhere.orders.map((other, index) => (
                        <FlexRow key={other.orderId} gap="xxs" align="baseline">
                            <OrderLink orderId={other.orderId} number={other.number} accent onOpen={onOpenOrder} />
                            {elsewhere.orders.length > 1 && other.count > 1 && <FlexRow>{`(${other.count})`}</FlexRow>}
                            {index < elsewhere.orders.length - 1 && ','}
                        </FlexRow>
                    ))}
                </FlexRow>
            )}
            {buttons}
        </FlexColumn>
    );
}

interface OwnPlacementCardViewProps {
    card: OwnPlacementCard;
    /** The other orders whose placements of the same type may bring the same container again. */
    alike: OtherOrder[];
    onOpenOrder: (orderId: string) => void;
}

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function OwnPlacementCardView({ card, alike, onOpenOrder }: OwnPlacementCardViewProps) {
    const i18n = useTranslate('');

    return (
        <ActionCardView
            action={card.action}
            count={card.count}
            flag={placementText(card, i18n)}
            flagClassName={classes.flag}
            className={classes.planned}
        >
            {alike.length > 0 && (
                <FlexRow gap="xxs" wrap="wrap" align="baseline" className={classes.doublePlanned}>
                    {`${i18n(DOUBLE_PLANNED)} ${i18n('Auch geplant in')}`}
                    {alike.map((other, index) => (
                        <FlexRow key={other.orderId} gap="xxs" align="baseline">
                            <OrderLink orderId={other.orderId} number={other.number} accent onOpen={onOpenOrder} />
                            {index < alike.length - 1 && ','}
                        </FlexRow>
                    ))}
                </FlexRow>
            )}
        </ActionCardView>
    );
}

interface ActionCardViewProps {
    action: ApiContainerAction;
    count?: number;
    flag: string;
    flagClassName: string;
    className?: string;
    children?: React.ReactNode;
}

/** A container of this order that is not in the stock yet, for example one it places. */
// @ai-generated 2026-10-26T13:28:00+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function ActionCardView({ action, count = 1, flag, flagClassName, className = '', children }: ActionCardViewProps) {
    const title = titleOf(action.container_type.display_string, action.container.display_string, count);

    return (
        <FlexColumn label={title} gap="xxs" align="stretch" className={`${classes.card} ${className}`}>
            <FlexRow className={classes.cardTitle}>{title}</FlexRow>
            {action.content.display_string && (
                <FlexRow className={classes.meta}>{action.content.display_string}</FlexRow>
            )}
            <FlexRow className={flagClassName}>{flag}</FlexRow>
            {children}
        </FlexColumn>
    );
}

interface ComingCardViewProps {
    card: ComingCard;
    onOpenOrder: (orderId: string) => void;
    buttons: React.ReactNode;
}

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function ComingCardView({ card, buttons, onOpenOrder }: ComingCardViewProps) {
    const i18n = useTranslate('');
    const { planned } = card;

    const title = titleOf(planned.container_type.display_string, planned.container.display_string, card.count);

    return (
        <FlexColumn label={title} gap="xxs" align="stretch" className={`${classes.card} ${classes.planned}`}>
            <FlexRow className={classes.cardTitle}>{title}</FlexRow>
            {planned.content.display_string && (
                <FlexRow className={classes.meta}>{planned.content.display_string}</FlexRow>
            )}
            {card.handledInPresentOrder && (
                <FlexRow className={classes.flag}>{comingHandledText(card, card.handledInPresentOrder, i18n)}</FlexRow>
            )}
            <FlexRow gap="xxs" className={classes.muted}>
                {i18n(planned.exchange_id ? 'kommt mit Tausch in' : 'kommt mit')}
                <OrderLink orderId={planned.source_id} number={planned.source_number} accent onOpen={onOpenOrder} />
            </FlexRow>
            {buttons}
        </FlexColumn>
    );
}

const comingHandledText = (card: ComingCard, kind: ContainerActionKind, i18n: (text: string) => string) =>
    card.count > 1 ? `${i18n(HANDLED[kind])} (${card.takenInPresentOrder} von ${card.count})` : i18n(HANDLED[kind]);

const withNumber = (type: string, number: string) => (number ? `${type} Nr. ${number}` : type);

const titleOf = (type: string, number: string, count: number) =>
    count > 1 ? `${count} × ${type}` : withNumber(type, number);

const placementText = (card: OwnPlacementCard, i18n: (text: string) => string) => {
    if (card.confirmedCount === card.count) {
        return i18n(CONFIRMED.place);
    }

    return card.confirmedCount > 0
        ? `${i18n(HANDLED.place)} · ${card.confirmedCount} ${i18n('bestätigt')}`
        : i18n(HANDLED.place);
};

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function handledText(card: StockCard, i18n: (text: string) => string): string {
    const kind = card.handledInPresentOrder ?? 'collect';

    if (!card.isPool) {
        return i18n(card.confirmedInPresentOrder > 0 ? CONFIRMED[kind] : HANDLED[kind]);
    }

    const confirmed = card.confirmedInPresentOrder > 0 ? ` · ${card.confirmedInPresentOrder} ${i18n('bestätigt')}` : '';
    return `${i18n(HANDLED[kind])} (${card.takenInPresentOrder} von ${card.group.count})${confirmed}`;
}
